import { type ReactNode } from 'react';
import { MONATE, tagText } from '../../../shared/crm';
import { RST, type RStatus } from '../../../shared/buchhaltung';
import { api } from '../../api';
import { SeitenKopf, Symbol, vars } from '../../komponenten/Grundbausteine';
import { zaehlerNeu } from '../../daten';

// Gemeinsame Bausteine der Buchhaltung (Prototyp bhHead, segB, zeitWahl, pdfBtn, setRow, rSt)

export const bhAktion = <T = Record<string, unknown>>(name: string, daten: unknown) => api<T>(`/api/buchhaltung/aktion/${name}`, { methode: 'POST', daten });

/** Führt eine Aktion aus und meldet das Ergebnis als Hinweis; null bei Fehler. */
export async function bhTun<T extends Record<string, unknown> = Record<string, unknown>>(hinweis: (t: string) => void, name: string, daten: unknown, nachher?: (e: T) => void): Promise<T | null> {
  try {
    const r = await bhAktion<T & { text?: string }>(name, daten);
    if (r.text) hinweis(r.text);
    zaehlerNeu();
    nachher?.(r);
    return r;
  } catch (e) {
    hinweis(`Geht nicht: ${(e as Error).message}`);
    return null;
  }
}

export const Kopf = ({ titel, meta, children }: { titel: string; meta: ReactNode; children?: ReactNode }) => <SeitenKopf titel={titel} zusatz={meta} id="h-bh">{children}</SeitenKopf>;

/** Segment wie im Prototyp segB (scrollbar, Breite nach Inhalt) */
export function SegB<T extends string>({ wahl, wert, setzen, attr }: { wahl: [T, string][]; wert: T; setzen: (w: T) => void; attr: string }) {
  const i = Math.max(0, wahl.findIndex(x => x[0] === wert));
  return <div className="seg-scroll" style={{ minWidth: 0 }}><div className="seg seg-sm" style={vars({ '--n': wahl.length, '--i': i, width: 'max-content' })} role="group" aria-label="Ansicht"><span className="seg-thumb" aria-hidden="true" />
    {wahl.map(([k, l]) => <button key={k} aria-pressed={k === wert} {...{ [`data-${attr}`]: k }} onClick={() => k !== wert && setzen(k)}>{l}</button>)}</div></div>;
}

export const monatText = (m: string) => `${MONATE[Number(m.slice(5, 7)) - 1]} ${m.slice(0, 4)}`;

/** Zeitraum-Auswahl (Prototyp zeitWahl): ganzes Jahr oder ein Monat, gruppiert nach Jahr */
export function ZeitWahl({ wert, setzen, monate, beschriftung = monatText, mitAlle = true, attr }: { wert: string; setzen: (w: string) => void; monate: string[]; beschriftung?: (m: string) => string; mitAlle?: boolean; attr: string }) {
  const jahre = [...new Set(monate.map(m => m.slice(0, 4)))];
  return <label className="pos" style={{ gap: 'var(--s2)' }}><span className="meta">Zeitraum</span><select className="input" data-bzw={attr} style={{ height: 32, width: 'auto', paddingLeft: 'var(--s3)' }} value={wert} onChange={e => setzen(e.target.value)}>
    {mitAlle && <option value="alle">Ganzes Jahr {jahre[0] ?? new Date().getFullYear()}</option>}
    {jahre.map(y => <optgroup key={y} label={y}>{monate.filter(m => m.startsWith(y)).map(m => <option key={m} value={m}>{beschriftung(m)}</option>)}</optgroup>)}</select></label>;
}

/** Öffnen: Rechnungs-PDFs und Belege liegen in Google Drive; gibt es (noch) keine, ein klarer Hinweis */
export function PdfKnopf({ text = 'Öffnen', url, hinweis }: { text?: string; url?: string; hinweis: (t: string) => void }) {
  return <button className="btn btn-sm btn-ghost" data-bpdf onClick={e => { e.stopPropagation(); if (url) window.open(url, '_blank', 'noopener'); else hinweis('Zu diesem Eintrag gibt es noch keine PDF; sie entsteht beim Versand aus der Google-Docs-Vorlage.'); }}><Symbol id="folder" klein />{text}</button>;
}

export const Einst = ({ t, d, children }: { t: string; d?: string; children: ReactNode }) =>
  <div className="row-set"><div><span style={{ fontWeight: 500 }}>{t}</span>{d && <span className="meta">{d}</span>}</div><div className="ctl">{children}</div></div>;

export const Status = ({ st }: { st: RStatus }) => <span className={`state ${RST[st][1]}`}>{RST[st][0]}</span>;
export const tag = (iso: string | null | undefined) => iso ? tagText(iso) : '–';
export const datum = (iso: string | null | undefined) => iso ? iso.split('-').reverse().join('.') : '–';

/** Datei als Base64 für den Aktionskatalog */
export const leseDatei = (f: File) => new Promise<{ name: string; typ: string; inhalt: string }>((ok, nein) => {
  const r = new FileReader();
  r.onload = () => ok({ name: f.name, typ: f.type || 'application/pdf', inhalt: String(r.result).split(',')[1] ?? '' });
  r.onerror = () => nein(r.error);
  r.readAsDataURL(f);
});
